/*
 * PageWright -- one copy per digital product: cart-level quantity clamp.
 *
 * Paul, 2026-09-28: customers must NOT be able to buy more than 1 copy of any digital product
 * (ebook, digibite, project) in one order line. The theme already hides the quantity stepper and
 * blocks duplicate adds (2026-07-27), yet ten ebook lines at quantity 2-3 still reached
 * fulfillment. The theme's add-time checks are UX; they cannot stop a cart that ARRIVES over the
 * limit: a cart permalink (/cart/add?quantity=N, /cart/VARIANT:QTY), a direct /cart/add.js, a
 * /cart/update.js. This file is the layer that repairs the cart itself.
 *
 * WHAT IT DOES
 *   On every page load, on bfcache restore / tab return, and after ANY cart mutation made
 *   through fetch (add / update / change / clear -- the theme's own drawer, quick-add, other
 *   scripts), it reads /cart.js and sets every cart LINE that is digital and has quantity > 1
 *   back to 1 via /cart/change (by line key). Then it refreshes the cart UI and shows one short,
 *   dismissable, non-blocking note.
 *
 * DIGITAL = the theme's EXISTING marker: the /cart.js line item's `requires_shipping === false`,
 * the same variant.requires_shipping signal blocks/quantity.liquid, snippets/cart-products.liquid
 * and product-form.js already use. No new marker. A line with no such field is NEVER touched:
 * unknown is not digital, so physical products can never be clamped by mistake.
 * EXCEPTION: gift cards are requires_shipping:false too, but `gift_card: true` marks them and
 * they are skipped (2 x $25 is a legitimate order). Any other non-shipping, non-digital product
 * (services, tickets, donations, subscriptions) cannot be recognised from theme code.
 *
 * PER LINE, NEVER PER VARIANT. A page cart (BAP / pw-page-cart.js) is N lines at quantity 1 that
 * share three tier variants. Grouping by variant or capping a cart total would break every
 * multi-page purchase, so this file never looks at variant ids. A 26-page cart makes zero writes.
 * (The same page added twice with identical properties merges into ONE line at quantity 2: that
 * is a second copy of one asset and is clamped, matching the plan's assumption A2.)
 *
 * WHAT IT CANNOT DO. A permalink that goes straight to CHECKOUT never loads the theme at all;
 * only a Shopify checkout validation Function stops that (plan layer L3). The server-side
 * backstop (plan layer L1) caps the entitlement either way.
 *
 * Failure policy: every failure path leaves the cart untouched and shows nothing. A note is shown
 * only after at least one line was actually changed.
 */
(function () {
  'use strict';

  var NOTE_ID = 'pw-digital-qty-note';
  var STYLE_ID = 'pw-digital-qty-note-style';
  var NOTE_TEXT = 'Digital products are limited to 1 copy per order, so we set the quantity back to 1.';
  var MUTATING = /\/cart\/(add|update|change|clear)(\.js)?(\?|$)/;
  var COLLAPSE_MS = (typeof window.PW_DIGITAL_QTY_COLLAPSE_MS === 'number' && window.PW_DIGITAL_QTY_COLLAPSE_MS) || 8000;
  var RECHECK_MIN_MS = 30000;

  var nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;
  if (!nativeFetch) return;

  var running = false;
  var again = false;
  var current = Promise.resolve();
  var lastCheckAt = 0;
  /* >0 while the clamp itself is calling window.fetch, so the wrapper below does not re-trigger. */
  var internalCalls = 0;

  function noop() {}

  /* Route table: honour the theme's (localized) routes, fall back to the root-relative paths. */
  function routes() {
    var t = (window.Theme && window.Theme.routes) || {};
    return {
      cart: (t.cart_url || '/cart') + '.js',
      change: t.cart_change_url || '/cart/change.js',
    };
  }

  function readCart() {
    return nativeFetch(routes().cart, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  /* Digital lines above quantity 1. Strict: requires_shipping must be exactly false. */
  function overQuantityLines(cart) {
    var items = (cart && cart.items) || [];
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (it && it.requires_shipping === false && it.gift_card !== true && it.quantity > 1 && it.key) out.push(it);
    }
    return out;
  }

  /* Goes through the CURRENT window.fetch (not the one captured at load) on purpose: if
   * pw-page-cart.js wrapped fetch before or after this file, its own guarded re-tier then sees
   * this /cart/change like any other and settles page tiers itself, with its own in-flight guard.
   * `internalCalls` stops OUR wrapper from re-triggering a check for our own request. */
  function setLineToOne(item) {
    var req;
    internalCalls++;
    try {
      req = window.fetch(routes().change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: item.key, quantity: 1 }),
      });
    } catch (e) {
      req = Promise.reject(e);
    } finally {
      internalCalls--;
    }
    return req.then(function (r) {
      var out = { ok: !!(r && r.ok), status: r && r.status, itemCount: undefined };
      if (!out.ok) return out;
      /* /cart/change returns the updated cart: its item_count feeds the header bubble. */
      return r.clone().json().then(function (cart) {
        if (cart && typeof cart.item_count === 'number') out.itemCount = cart.item_count;
        return out;
      }, function () {
        return out;
      });
    }, function () {
      return { ok: false, status: 0, itemCount: undefined };
    });
  }

  function clampLines(lines) {
    var result = { clamped: 0, failed: 0, lastStatus: 0, itemCount: undefined };
    var chain = Promise.resolve();
    lines.forEach(function (item) {
      chain = chain.then(function () {
        return setLineToOne(item).then(function (o) {
          if (o.ok) {
            result.clamped++;
            if (typeof o.itemCount === 'number') result.itemCount = o.itemCount;
          } else {
            result.failed++;
            result.lastStatus = o.status;
          }
        });
      });
    });
    return chain.then(function () {
      return result;
    });
  }

  /* PII-free outcome signal: counts and an HTTP status only (no keys, ids, titles, emails). A
   * failed clamp must not be silent: console warning + a DOM event analytics can listen for. */
  function report(result) {
    try {
      if (result.readFailed) {
        console.warn('[pw-digital-qty-clamp] /cart.js read failed, clamp did not run: ' + (result.error || 'unknown'));
      }
      if (result.failed) {
        console.warn('[pw-digital-qty-clamp] /cart/change failed for ' + result.failed + ' line(s), status ' + result.lastStatus);
      }
      if (typeof window.CustomEvent === 'function' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new window.CustomEvent('pw:digital-qty-clamp', {
          detail: { clamped: result.clamped, failed: result.failed, status: result.lastStatus, readFailed: !!result.readFailed },
        }));
      }
    } catch (e) { /* reporting must never break the cart */ }
  }

  /* Bring the visible cart in line with the repaired one, through the theme's OWN mechanism.
   *
   * Horizon re-renders the cart only in response to the native 'shopify:cart:lines-update' event
   * (@shopify/events CartLinesUpdateEvent): cart-items-component (drawer AND /cart page) awaits
   * event.promise and, when detail.sections has no HTML for it, re-renders its section via
   * sectionRenderer.renderSection (its documented path for external cart updates); cart-icon reads
   * detail.itemCount for the header bubble. That is the same event the theme's own +/- buttons
   * dispatch. Action is 'update': 'add' would auto-open the drawer.
   *
   * WHY A PLAIN Event AND NO import(): this file is a classic script. An earlier version did
   * `import('@theme/section-renderer')`; Shopify's asset pipeline transpiles theme JS and rewrote
   * that into a require() shim, which throws in the browser, and the .catch swallowed it, so the
   * server cart was clamped while the visible cart kept showing the old quantity and total. The
   * standard events are plain DOM Events (name + properties), so constructing one needs no module.
   * `cart: null` is deliberate: the listeners fall back to detail.itemCount. */
  function refreshUI(itemCount) {
    try {
      if (window.PWPageCart && typeof window.PWPageCart.refreshCartBubble === 'function') {
        window.PWPageCart.refreshCartBubble();
      }
    } catch (e) { /* cosmetic */ }
    var counted = typeof itemCount === 'number' ? Promise.resolve(itemCount) : readCart().then(function (c) {
      return c && typeof c.item_count === 'number' ? c.item_count : undefined;
    }, function () {
      return undefined;
    });
    return counted.then(function (count) {
      if (typeof count !== 'number') return; /* cannot report a count: do not set the bubble to 0 */
      try {
        var ev = new window.Event('shopify:cart:lines-update', { bubbles: true, cancelable: true });
        ev.action = 'update';
        ev.lines = [];
        ev.promise = Promise.resolve({
          cart: null,
          detail: { itemCount: count, source: 'pw-digital-qty-clamp', didError: false },
        });
        document.dispatchEvent(ev);
      } catch (e) { /* cosmetic */ }
    });
  }

  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    /* Mobile-first: 44px tap target, 15px text, 16px gutters. Default layout is the FIXED fallback
     * for pages with no cart UI. Inside the cart drawer or on /cart the note is IN-FLOW instead
     * (--inflow): the drawer's Checkout / accelerated buttons live in a sticky bottom summary and a
     * fixed note would cover them. --collapsed keeps the live region in the DOM for screen readers
     * but takes it out of sight and layout. */
    s.textContent =
      '#' + NOTE_ID + '{position:fixed;top:auto;left:16px;right:16px;bottom:max(16px,env(safe-area-inset-bottom));' +
      'margin:0 auto;width:auto;max-width:480px;box-sizing:border-box;display:flex;align-items:center;gap:8px;' +
      'padding:8px 8px 8px 16px;z-index:2147483000;' +
      'background:var(--color-background,#fff);color:var(--color-foreground,#111);' +
      'border:1px solid var(--color-foreground,#111);border-radius:4px;box-shadow:0 4px 16px rgba(0,0,0,.2);' +
      'font-size:15px;line-height:1.4;font-family:inherit}' +
      '#' + NOTE_ID + '.pw-digital-qty-note--inflow{position:static;left:auto;right:auto;bottom:auto;max-width:none;' +
      'margin:12px 16px;z-index:auto;box-shadow:none}' +
      '@media (min-width:750px){#' + NOTE_ID + '.pw-digital-qty-note--inflow{max-width:720px;margin-inline:auto}}' +
      '#' + NOTE_ID + '.pw-digital-qty-note--collapsed{position:absolute;width:1px;height:1px;margin:-1px;padding:0;' +
      'overflow:hidden;clip-path:inset(50%);white-space:nowrap;border:0;box-shadow:none}' +
      '#' + NOTE_ID + ' .pw-digital-qty-note__text{flex:1 1 auto;min-width:0}' +
      '#' + NOTE_ID + ' .pw-digital-qty-note__dismiss[hidden]{display:none}' +
      '#' + NOTE_ID + ' .pw-digital-qty-note__dismiss{flex:0 0 auto;min-width:44px;min-height:44px;padding:0;' +
      'display:inline-flex;align-items:center;justify-content:center;background:none;border:0;border-radius:4px;' +
      'color:inherit;font-size:24px;line-height:1;cursor:pointer}';
    (document.head || document.body).appendChild(s);
  }

  var noteObserver = null;
  var collapseTimer = null;

  /* Where the note lives. Two hard constraints:
   *   1. Never over the sticky Checkout summary (so never position:fixed inside the cart UI).
   *   2. Never INSIDE a subtree Horizon morphs. After the clamp the cart is re-rendered through
   *      sectionRenderer/morph, which pairs children by position: a foreign node inside the morphed
   *      tree is matched with a real node or removed. Confirmed live on staging: the morph turned
   *      the note into a wrapper around the cart items. Morphed regions are the /cart section
   *      element's children and the drawer's .cart-drawer__inner (data-hydration-key) subtree.
   * So the note is an in-flow SIBLING just outside them:
   *   - open CART DRAWER (a MODAL <dialog> inside <theme-drawer id="cart-drawer">; everything outside
   *     it is inert, so the note must be inside it): directly before .cart-drawer__inner. Scoped to
   *     the drawer on purpose: any other open modal (newsletter, quick-add) never receives the note;
   *   - /cart page: directly before the .shopify-section that holds .cart-page;
   *   - anywhere else there is no cart UI to collide with: fixed, on <body>. */
  function noteAnchor() {
    var dlg = document.querySelector('#cart-drawer dialog[open]');
    if (dlg) {
      var inner = dlg.querySelector('.cart-drawer__inner');
      if (inner && inner.parentNode) return { parent: inner.parentNode, before: inner, inflow: true };
      return { parent: dlg, before: null, inflow: true };
    }
    var page = document.querySelector('.cart-page');
    var section = page && page.closest ? page.closest('.shopify-section') : null;
    if (section && section.parentNode) return { parent: section.parentNode, before: section, inflow: true };
    return { parent: document.body, before: null, inflow: false };
  }

  function placeNote(note) {
    var a = noteAnchor();
    var cls = 'pw-digital-qty-note' + (a.inflow ? ' pw-digital-qty-note--inflow' : '') +
      (/--collapsed/.test(note.className || '') ? ' pw-digital-qty-note--collapsed' : '');
    if (note.className !== cls) note.className = cls;
    /* `before` can BE the note (it is already the first child): that is in place. Re-inserting it
     * would be a DOM change the observer sees again, forever. */
    var inPlace = note.parentNode === a.parent && (!a.before || a.before === note || note.nextSibling === a.before);
    if (!inPlace) a.parent.insertBefore(note, a.before);
  }

  function stopWatching() {
    if (noteObserver) {
      noteObserver.disconnect();
      noteObserver = null;
    }
    if (collapseTimer) {
      clearTimeout(collapseTimer);
      collapseTimer = null;
    }
  }

  /* One polite status note. Not a dialog, no focus move. After a few seconds it collapses to a
   * visually hidden live region (still there for screen readers) and the DOM watch is stopped. */
  function showNote() {
    try {
      ensureStyle();
      stopWatching();
      var old = document.getElementById(NOTE_ID);
      if (old) old.remove();
      var note = document.createElement('div');
      note.id = NOTE_ID;
      note.setAttribute('role', 'status');
      note.setAttribute('aria-live', 'polite');
      note.setAttribute('aria-atomic', 'true');

      var text = document.createElement('span');
      text.className = 'pw-digital-qty-note__text';

      var btn = document.createElement('button');
      btn.setAttribute('type', 'button');
      btn.setAttribute('aria-label', 'Dismiss message');
      btn.className = 'pw-digital-qty-note__dismiss';
      btn.textContent = '×';
      btn.addEventListener('click', function () {
        stopWatching();
        note.remove();
      });

      note.appendChild(text);
      note.appendChild(btn);
      placeNote(note);

      /* The drawer can open, or its section re-render (which drops foreign children), while the
       * note is up: put it back. Our own move is a no-op on the next pass. Lives only until the
       * note collapses or is dismissed. */
      if (typeof window.MutationObserver === 'function') {
        noteObserver = new window.MutationObserver(function () {
          placeNote(note);
        });
        noteObserver.observe(document.documentElement, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['open'],
        });
      }

      collapseTimer = setTimeout(function () {
        stopWatching();
        note.className = (note.className || '') + ' pw-digital-qty-note--collapsed';
        btn.setAttribute('hidden', '');
        btn.setAttribute('tabindex', '-1');
      }, COLLAPSE_MS);

      /* Text goes in one tick AFTER the live region is in the DOM, so assistive tech announces it. */
      (window.requestAnimationFrame || window.setTimeout)(function () {
        text.textContent = NOTE_TEXT;
      });
    } catch (e) { /* never let the note break the cart */ }
  }

  function check() {
    if (running) {
      again = true;
      return current;
    }
    running = true;
    lastCheckAt = Date.now();
    var readFailed = false;
    current = readCart()
      .catch(function (err) {
        /* A failed read must not be silent (it means no clamp ran): warn + the same pw event. */
        readFailed = true;
        report({ clamped: 0, failed: 0, lastStatus: 0, readFailed: true, error: err && err.message });
        return null;
      })
      .then(function (cart) {
        if (readFailed) return null;
        var lines = overQuantityLines(cart);
        if (!lines.length) return null;
        return clampLines(lines);
      })
      .then(function (result) {
        if (!result) return;
        report(result);
        if (!result.clamped) return;
        return refreshUI(result.itemCount).then(showNote);
      })
      .catch(noop)
      .then(function () {
        running = false;
        if (again) {
          again = false;
          return check();
        }
      });
    return current;
  }

  /* Any cart mutation made through fetch by anyone else: re-check once it lands. Requests the
   * clamp makes itself are marked by internalCalls (read synchronously at call time). */
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isOwn = internalCalls > 0;
    var promise = nativeFetch(input, init);
    if (!isOwn && MUTATING.test(String(url))) {
      promise.then(function () { setTimeout(check, 0); }, noop);
    }
    return promise;
  };

  /* Initial load is handled below; pageshow only matters for a bfcache RESTORE. Tab return is
   * throttled so switching tabs does not cost a cart read every time. */
  window.addEventListener('pageshow', function (e) {
    if (e && e.persisted) check();
  });
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden && Date.now() - lastCheckAt > RECHECK_MIN_MS) check();
  });

  window.PWDigitalQtyClamp = {
    check: check,
    overQuantityLines: overQuantityLines,
    idle: function () { return current; },
  };

  /* The server renders the cart's item count into the page (layout/theme.liquid). An empty cart
   * cannot hold an over-limit line, so skip the initial read. Every other trigger still checks. */
  function initialCheck() {
    if (window.PW_CART_ITEM_COUNT === 0) return;
    check();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialCheck);
  } else {
    initialCheck();
  }
})();
